import { call, delay, put, select, takeLatest } from "redux-saga/effects";
import i18n from "../../i18n";
import { sessionApi } from "../../services/session.api";
import {
  Session,
  ValidateSessionSuccessPayload,
} from "../../types/session.definition";
import {
  getDashboardUrl,
  getLoginApplicationUrl,
} from "../../utils/config-utils";
import { clearPersistedPinLock } from "../../utils/pin-utils";
import { removeMedecine360SessionTokenFromStorage } from "../../utils/session-utils";
import {
  clearSession,
  getSessionFailure,
  getSessionRequest,
  getSessionSuccess,
  forceLogin,
  pinAttemptsExceeded,
  requestTerminateSessionRequest,
  validateSessionFailure,
  validateSessionRequest,
  validateSessionSuccess,
} from "../reducers/sessionSlice";
import type { RootState } from "../store";

function* validateSessionSaga() {
  try {
    const response: ValidateSessionSuccessPayload = yield call(
      sessionApi.validateSession
    );
    yield put(
      validateSessionSuccess({
        ...response,
        sessionStatus: "VALID_SESSION",
      })
    );
    removeMedecine360SessionTokenFromStorage();
    yield put(getSessionRequest());
  } catch (error) {
    console.error("Session validation failed:", error);

    // Handle different types of errors
    let errorMessage = "Failed to validate session";
    if (error instanceof Error) {
      errorMessage = error.message;
    } else if (typeof error === "object" && error !== null) {
      // Handle structured error objects
      const errorObj = error as { code?: string; message?: string }; // Type assertion for error handling
      if (
        errorObj.code === "NETWORK_ERROR" ||
        errorObj.code === "TIMEOUT_ERROR"
      ) {
        errorMessage = "Network error - please check your connection";
      } else if (
        errorObj.code === "UNAUTHORIZED" ||
        errorObj.code === "FORBIDDEN"
      ) {
        errorMessage = "Authentication failed - please log in again";
      } else if (errorObj.message) {
        errorMessage = errorObj.message;
      }
    }

    yield put(validateSessionFailure(errorMessage));
  }
}

/** MED-1621: GET /session answers a partial payload while the session is locked or has no PIN. */
const isPartialSession = (session: Session | null | undefined) =>
  session?.locked === true || session?.hasPin === false;

export function* getSessionSaga() {
  try {
    const session: Session = yield call(sessionApi.getSession);
    const current: Session | null = yield select(
      (state: RootState) => state.session.session
    );
    // A refresh while locked must not replace the full session the open pages are using.
    yield put(
      getSessionSuccess(
        isPartialSession(session) && current && !isPartialSession(current)
          ? current
          : session
      )
    );
  } catch (error) {

    // If getSession fails, the session might be invalid
    yield put(
      getSessionFailure(
        error instanceof Error ? error.message : "Failed to get session"
      )
    );
    // Set session status to invalid so user gets redirected to external auth
    yield put(validateSessionFailure("Session data could not be retrieved"));
  }
}

export function* requestTerminateSessionSaga() {
  try {
    yield call(sessionApi.terminateSession);
  } catch {
    // Ignore errors during logout - we're logging out anyway
  } finally {
    // Always clear session and redirect, even if API call fails.
    // The redirect lives here, after the logout request has completed: a component that
    // navigates while it is in flight makes the browser abort it (MED-1395).
    yield put(clearSession());
    removeMedecine360SessionTokenFromStorage();
    // MED-1643: the next session must not open locked (sign out from the menu or the lock screen).
    clearPersistedPinLock();
    redirectToLogin();
  }
}

function redirectToLogin() {
  const redirectTo = getDashboardUrl();
  const language = i18n.language || localStorage.getItem("language") || "fr";
  const loginUrl = new URL(getLoginApplicationUrl(redirectTo));
  loginUrl.searchParams.set("language", language);
  window.location.href = loginUrl.toString();
}

/**
 * MED-1621: five wrong PINs — the server has revoked every token of the user, so there is
 * nothing to log out from: clear the session and go to the login page.
 */
export function* pinAttemptsExceededSaga() {
  yield put(clearSession());
  removeMedecine360SessionTokenFromStorage();
  redirectToLogin();
}

/** Time the "session closed" message stays on screen before the login page. */
export const FORCED_LOGIN_DELAY_MS = 3000;

/**
 * MED-1621 security answers: 403 PIN_CREATE_REAUTH_REQUIRED (password login older than 10 min
 * when creating the PIN) and 401 SESSION_REUSE_DETECTED (every session of the user revoked).
 * PinLockProvider shows the message; the session is cleared and the login page opened (it
 * returns to the dashboard).
 */
export function* forceLoginSaga() {
  yield put(clearSession());
  removeMedecine360SessionTokenFromStorage();
  yield delay(FORCED_LOGIN_DELAY_MS);
  redirectToLogin();
}

export function* watchSession() {
  yield takeLatest(validateSessionRequest.type, validateSessionSaga);
  yield takeLatest(getSessionRequest.type, getSessionSaga);
  yield takeLatest(
    requestTerminateSessionRequest.type,
    requestTerminateSessionSaga
  );
  yield takeLatest(pinAttemptsExceeded.type, pinAttemptsExceededSaga);
  yield takeLatest(forceLogin.type, forceLoginSaga);
}
